import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import SecureSessionService from '../services/session/SecureSessionService';

export interface SessionData {
  sessionId: string;
  userId: string;
  expiresAt: number;
}

interface SessionContextType {
  sessionData: SessionData | null;
  setSessionData: (data: SessionData) => void;
  clearSession: () => void;
  isSessionValid: () => boolean;
  forceSessionExpired: boolean;
  setForceSessionExpired: (expired: boolean) => void;
}

const SessionContext = createContext<SessionContextType | undefined>(undefined);

export const SessionProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [sessionData, setSessionDataState] = useState<SessionData | null>(() => {
    return SecureSessionService.getStoredSession();
  });
  const [forceSessionExpired, setForceSessionExpired] = useState(false);

  useEffect(() => {
    const handleSessionExpired = () => {
      setForceSessionExpired(true);
    };

    const handleSessionRefreshed = (e: Event) => {
      const { expiresAt } = (e as CustomEvent).detail;
      const session = SecureSessionService.getStoredSession();
      if (session && expiresAt > 0) {
        setSessionDataState({
          sessionId: session.sessionId,
          userId: session.userId,
          expiresAt
        });
      }
    };

    window.addEventListener('session-expired', handleSessionExpired);
    window.addEventListener('session-refreshed', handleSessionRefreshed);
    return () => {
      window.removeEventListener('session-expired', handleSessionExpired);
      window.removeEventListener('session-refreshed', handleSessionRefreshed);
    };
  }, []);

  const setSessionData = (data: SessionData) => {
    SecureSessionService.storeSession(data);
    setSessionDataState(data);
  };

  const clearSession = () => {
    SecureSessionService.clearSession();
    setSessionDataState(null);
  };

  const isSessionValid = () => {
    if (!sessionData) return false;
    return !SecureSessionService.isSessionExpired(sessionData.expiresAt);
  };

  return (
    <SessionContext.Provider value={{
      sessionData,
      setSessionData,
      clearSession,
      isSessionValid,
      forceSessionExpired,
      setForceSessionExpired
    }}>
      {children}
    </SessionContext.Provider>
  );
};

export const useSession = () => {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error('useSession must be used within SessionProvider');
  }
  return context;
};
